import React from 'react';
import SEO from '../../components/SEO';
import PageBanner from '../../components/PageBanner';
import { motion } from 'framer-motion';
import { Target, Compass, Award, Heart, ShieldCheck, Users } from 'lucide-react';

import { useLanguage } from '../../context/LanguageContext';
import { translations } from '../../data/translations';

const GGS = () => {
    const { language } = useLanguage();
    const t = translations[language].ggs;
    const values = t.values.map((item, index) => ({
        ...item,
        icon: [ShieldCheck, Users, Heart, Award][index]
    }));

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.6 }
        }
    };

    return (
        <div className="pt-20 lg:pt-[120px] min-h-screen bg-white">
            <SEO
                title="About GGS | Vision, Mission & Values"
                description="Discover the Vision, Mission, and core Values of Gulf Great Sands (GGS), the official partner of BAIC in the Oman."
                canonicalUrl="https://baicoman.com/about/ggs"
            />
            <h1 className="sr-only">About GGS - Vision, Mission & Values</h1>

            <PageBanner
                image="/assets/images/about/overview/new/bg.webp"
                imageAlt="GGS Vision"
                contentClassName="items-start text-left"
            >
                <motion.h2
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                    className="text-2xl md:text-7xl font-bold text-white mb-4 tracking-tight"
                    style={{
                        textShadow: "0 4px 12px rgba(0,0,0,0.9)"
                    }}
                >
                    {t.title}
                </motion.h2>

            </PageBanner>

            {/* Gulf Great Sands Section */}
            <div className="max-w-6xl mx-auto my-20 px-6">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    viewport={{ once: true }}
                    className="text-center max-w-4xl mx-auto mb-20"
                >
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 uppercase">
                        {t.subtitle}
                    </h2>
                    <div className="space-y-6 text-gray-600 leading-relaxed text-lg">
                        <p>
                            {t.p1}
                        </p>
                        <p>
                            {t.p2}
                        </p>
                    </div>
                </motion.div>
                {/* Vision & Mission Section */}
                <div className="mb-20">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-20">
                        {/* Vision Card */}
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                            className="bg-white p-10 md:p-12 rounded-[2.5rem] shadow-xl border border-gray-100 relative overflow-hidden group hover:-translate-y-2 transition-transform duration-500"
                        >
                            <div className="absolute top-0 right-0 w-40 h-40 bg-red-50 rounded-bl-[100px] -mr-10 -mt-10 transition-colors group-hover:bg-red-100"></div>
                            <div className="relative z-10">
                                <div className="w-16 h-16 bg-red-600 rounded-2xl flex items-center justify-center mb-8 shadow-lg shadow-red-600/30">
                                    <Target className="w-8 h-8 text-white" />
                                </div>
                                <h3 className="text-3xl font-bold text-gray-900 mb-6 uppercase tracking-tight">{t.vision.title}</h3>
                                <p className="text-xl md:text-2xl text-gray-600 font-light leading-relaxed">
                                    {t.vision.desc}
                                </p>
                            </div>
                        </motion.div>

                        {/* Mission Card */}
                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                            className="bg-white p-10 md:p-12 rounded-[2.5rem] shadow-xl border border-gray-100 relative overflow-hidden group hover:-translate-y-2 transition-transform duration-500"
                        >
                            <div className="absolute top-0 right-0 w-40 h-40 bg-gray-100 rounded-bl-[100px] -mr-10 -mt-10 transition-colors group-hover:bg-gray-200"></div>
                            <div className="relative z-10">
                                <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center mb-8 shadow-lg">
                                    <Compass className="w-8 h-8 text-white" />
                                </div>
                                <h3 className="text-3xl font-bold text-gray-900 mb-6 uppercase tracking-tight">{t.mission.title}</h3>
                                <p className="text-xl md:text-2xl text-gray-600 font-light leading-relaxed">
                                    {t.mission.desc}
                                </p>
                            </div>
                        </motion.div>
                    </div>
                </div>
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
                >
                    {values.map((val, index) => (
                        <motion.div
                            key={index}
                            variants={itemVariants}
                            className="group bg-white p-8 rounded-3xl border border-gray-100 shadow-sm hover:shadow-xl hover:border-red-100 transition-all duration-500 relative overflow-hidden text-center"
                        >
                            <div className="absolute inset-x-0 bottom-0 h-1 bg-red-600 scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-center"></div>

                            <div className="w-16 h-16 mx-auto bg-gray-50 rounded-full flex items-center justify-center mb-6 group-hover:bg-red-500 group-hover:text-white transition-colors duration-500">
                                <val.icon className="w-7 h-7" />
                            </div>

                            <h3 className="text-xl font-bold text-gray-900 mb-4 uppercase">{val.title}</h3>
                        </motion.div>
                    ))}
                </motion.div>
            </div>
        </div >
    );
};

export default GGS;